import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Chip,
  Tooltip,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Delete,
  ContentCopy,
  Search,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../../config/brand';

export interface EntityColumn<T> {
  id: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
}

export interface EntityDataTableProps<T extends { id: string }> {
  title?: string;
  data: T[];
  columns: EntityColumn<T>[];
  viewPath?: (row: T) => string;
  editPath?: (row: T) => string;
  onDelete?: (row: T) => void;
  statusField?: keyof T;
  statusColors?: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'>;
  searchFields?: (keyof T)[];
  loading?: boolean;
  actions?: React.ReactNode;
}

function truncateId(id: string): string {
  return id ? `${id.slice(0, 8)}…` : '—';
}

export function EntityDataTable<T extends { id: string }>({
  title,
  data,
  columns,
  viewPath,
  editPath,
  onDelete,
  statusField,
  statusColors = {},
  searchFields = [],
  loading,
  actions,
}: EntityDataTableProps<T>) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      searchFields.some((field) => {
        const val = row[field];
        return val != null && String(val).toLowerCase().includes(q);
      }) || row.id.toLowerCase().includes(q),
    );
  }, [data, search, searchFields]);

  const copyId = (id: string) => {
    navigator.clipboard.writeText(id);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {title && (
          <Typography variant="h6" sx={{ color: BRAND.colors.navy, fontWeight: 700 }}>
            {title}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          {actions}
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: `rgba(27, 77, 137, 0.08)` }}>
              <TableCell sx={{ fontWeight: 700, color: BRAND.colors.navy }}>ID</TableCell>
              {columns.map((col) => (
                <TableCell key={col.id} sx={{ fontWeight: 700, color: BRAND.colors.navy }}>
                  {col.label}
                </TableCell>
              ))}
              {statusField && (
                <TableCell sx={{ fontWeight: 700, color: BRAND.colors.navy }}>Status</TableCell>
              )}
              <TableCell sx={{ fontWeight: 700, color: BRAND.colors.navy }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 3} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 3} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {truncateId(row.id)}
                      </Typography>
                      <Tooltip title="Copy full ID">
                        <IconButton size="small" onClick={() => copyId(row.id)}>
                          <ContentCopy fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.id}>
                      {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.id] ?? '—')}
                    </TableCell>
                  ))}
                  {statusField && (
                    <TableCell>
                      <Chip
                        size="small"
                        label={String(row[statusField] ?? 'unknown')}
                        color={statusColors[String(row[statusField])] || 'default'}
                      />
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {viewPath && (
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => navigate(viewPath(row))}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {editPath && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => navigate(editPath(row))}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => onDelete(row)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default EntityDataTable;
